import * as v from 'valibot';
import { isToolUIPart, type UIMessage } from 'ai';
import {
  AIAssistantToolNames,
  CapturedAIAssistantGetViewImageOutput,
  type AIAssistantGetViewImageOutput,
} from 'balus-common';

type MessagePart = UIMessage['parts'][number];

function isCapturedViewImagePart(part: MessagePart): boolean {
  return (
    isToolUIPart(part) &&
    part.type === `tool-${AIAssistantToolNames.getViewImage}` &&
    part.state === 'output-available' &&
    // 出力そのものは作らない v.is を使う。送信のたびに全メッセージを走査するため
    v.is(CapturedAIAssistantGetViewImageOutput, part.output)
  );
}

type PartPosition = { messageIndex: number; partIndex: number };

function findLatestCapturedImage(messages: UIMessage[]): PartPosition | undefined {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex--) {
    const parts = messages[messageIndex].parts;
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex--) {
      if (isCapturedViewImagePart(parts[partIndex])) return { messageIndex, partIndex };
    }
  }
  return undefined;
}

function findLastUserIndex(messages: UIMessage[]): number {
  for (let index = messages.length - 1; index >= 0; index--) {
    if (messages[index].role === 'user') return index;
  }
  return -1;
}

// 画像本体を落とし、寸法とビュー情報だけ残す。落とした結果は clientTools の toModelOutput が
// 「参照できません」というテキストへ変換するので、モデルからは欠落ではなく説明として見える。
function stripImage(part: MessagePart): MessagePart {
  const output = (part as { output: AIAssistantGetViewImageOutput }).output;
  const { image: _image, ...rest } = output;
  return { ...part, output: rest } as MessagePart;
}

function stripCapturedImages(messages: UIMessage[], kept: PartPosition | undefined): UIMessage[] {
  let changed = false;

  const sanitized = messages.map((message, messageIndex) => {
    let messageChanged = false;
    const parts = message.parts.map((part, partIndex) => {
      if (!isCapturedViewImagePart(part)) return part;
      if (kept && kept.messageIndex === messageIndex && kept.partIndex === partIndex) return part;
      changed = true;
      messageChanged = true;
      return stripImage(part);
    });
    return messageChanged ? { ...message, parts } : message;
  });

  return changed ? sanitized : messages;
}

/**
 * チャット履歴へ保存するメッセージから、getViewImage が返した画像本体をすべて取り除く。
 *
 * 履歴の payload は1メッセージ 1,000,000 文字までで、保存はスレッド内の全メッセージを毎回
 * 書き直す。base64 の画像を1つ残すだけで上限を超え、以降そのスレッドの履歴保存が丸ごと
 * 失敗し続けるため、保存前に必ず落とす。
 */
export function sanitizeAIAssistantMessagesForHistory(messages: UIMessage[]): UIMessage[] {
  return stripCapturedImages(messages, undefined);
}

/**
 * サーバへ送るメッセージから、直近1枚を除くビュー画像を取り除く。
 *
 * messages はリクエストごとに全件送られ、モデリング1ターンの中でもツール往復のたびに
 * 送り直される（maxSteps は30）。画像を残したままだと同じ base64 を何度も再送することになる。
 * 添付画像が「最後の user メッセージのものだけモデルへ渡す」(expandAIChatImageParts) のと
 * 同じ考え方で、現在のターンの最新の1枚だけを残す。
 */
export function sanitizeAIAssistantMessagesForRequest(messages: UIMessage[]): UIMessage[] {
  const latest = findLatestCapturedImage(messages);
  // 現在のターンより前の画像は、AIが見返す前提の情報ではないので残さない
  const kept = latest && latest.messageIndex > findLastUserIndex(messages) ? latest : undefined;
  return stripCapturedImages(messages, kept);
}
